import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { resetOperation, setCommandMessage } from '../../../redux/operationSlice';
import { COMMANDS } from '../../../utils/commonProperties';
import { DrawCommand } from '../../../modules/undoRedo';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { postNote } from '../../../services/projectApi';
import { drawCalloutCanvas } from '../../../utils/drawCalloutCanvas';

// Mevcut kullanıcı için "Ad Soyad — Rol" başlığını üret. Rol, projedeki
// userRole (örn. "Elektrik Müellifi") veya share-link partyType'ından gelir.
function buildLocalHeader(userRole) {
  try {
    const u = JSON.parse(localStorage.getItem('projabze_user') || 'null');
    if (!u) return null;
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '';
    if (!name) return null;
    const parts = [name];
    if (userRole) parts.push(userRole);
    return parts.join(' — ');
  } catch { return null; }
}

const useDrawCallout = (scene, camera, renderer, snapPoints) => {
  const [step, setStep] = useState(0);
  const [targetPoint, setTargetPoint] = useState(null);
  const [boxPosition, setBoxPosition] = useState(null);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputScreenPos, setInputScreenPos] = useState({ x: 0, y: 0 });
  const [cancelled, setCancelled] = useState(false);

  // Edit mode state
  const [editingGroup, setEditingGroup] = useState(null);

  const hoveredPointRef = useRef(null);
  const snapMarkerRef = useRef(null);
  const tempLeaderRef = useRef(null);
  const inputRef = useRef(null);

  const dispatch = useDispatch();
  const commandType = useSelector(state => state.operation.commandType);
  const { gridMode, snapMode } = useSelector(state => state.mods);
  const { activeLayerName } = useSelector(state => state.layer);
  const projectId = useSelector(state => state.project.projectId);
  const shareAccess = useSelector(state => state.project.shareAccess);
  const userRole = useSelector(state => state.projectTabs?.userRole || shareAccess?.partyRole || null);

  // Keep refs in sync for use inside closures (finalize, syncToServer)
  const projectIdRef = useRef(projectId);
  const shareTokenRef = useRef(shareAccess?.token);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  useEffect(() => { shareTokenRef.current = shareAccess?.token; }, [shareAccess]);

  const BOX_COLOR = 0x00ccff;
  const LEADER_COLOR = 0x00ccff;
  const TEXT_COLOR = '#00ccff';
  const FONT_SIZE = 14;
  const PADDING = 8;
  const BOX_WIDTH = 200;
  const BOX_HEIGHT = 60;

  // Calculate the nearest point on the box rectangle edge to the target point
  const getNearestBoxEdgePoint = useCallback((target, boxPos, spriteWorldWidth, spriteWorldHeight) => {
    // Box corners (anchor is top-left, extends right and down)
    const left = boxPos.x;
    const right = boxPos.x + spriteWorldWidth;
    const top = boxPos.y;
    const bottom = boxPos.y - spriteWorldHeight;

    // Clamp the target point to the box edge
    const cx = Math.max(left, Math.min(right, target.x));
    const cy = Math.max(bottom, Math.min(top, target.y));

    // If the clamped point is inside the box, project to nearest edge
    if (cx > left && cx < right && cy > bottom && cy < top) {
      const dLeft = cx - left;
      const dRight = right - cx;
      const dTop = top - cy;
      const dBottom = cy - bottom;
      const minDist = Math.min(dLeft, dRight, dTop, dBottom);
      if (minDist === dLeft) return new THREE.Vector3(left, cy, 0);
      if (minDist === dRight) return new THREE.Vector3(right, cy, 0);
      if (minDist === dTop) return new THREE.Vector3(cx, top, 0);
      return new THREE.Vector3(cx, bottom, 0);
    }

    return new THREE.Vector3(cx, cy, 0);
  }, []);

  // Get sprite dimensions in world coords (header'a göre canvas oranı)
  const getSpriteWorldSize = useCallback((cssW = BOX_WIDTH, cssH = BOX_HEIGHT) => {
    const viewHeight = camera?.top ? (camera.top - camera.bottom) : 500;
    const spriteWorldWidth = viewHeight * 0.2;
    const spriteWorldHeight = spriteWorldWidth * (cssH / cssW);
    return { spriteWorldWidth, spriteWorldHeight };
  }, [camera]);

  // Draw the leader line preview from target to current mouse
  const drawLeaderPreview = (from, to) => {
    if (tempLeaderRef.current) {
      scene.remove(tempLeaderRef.current);
      tempLeaderRef.current.geometry.dispose();
      tempLeaderRef.current.material.dispose();
    }
    const geometry = new THREE.BufferGeometry().setFromPoints([from, to]);
    const material = new THREE.LineBasicMaterial({ color: LEADER_COLOR });
    const line = new THREE.Line(geometry, material);
    scene.add(line);
    tempLeaderRef.current = line;
  };

  // Create the final callout group: leader line + box border + text sprite
  const createCalloutGroup = useCallback((target, boxPos, text) => {
    const group = new THREE.Group();

    // Header — yerel kullanıcının adı ve rolü ("Ad Soyad — Rol")
    const headerText = buildLocalHeader(userRole);

    const { canvas, width: cssW, height: cssH } = drawCalloutCanvas({
      header: headerText,
      text,
      color: TEXT_COLOR,
    });
    const { spriteWorldWidth, spriteWorldHeight } = getSpriteWorldSize(cssW, cssH);

    // Calculate nearest box edge point for leader line
    const edgePoint = getNearestBoxEdgePoint(target, boxPos, spriteWorldWidth, spriteWorldHeight);

    // Leader line (target point -> nearest box edge)
    const leaderGeometry = new THREE.BufferGeometry().setFromPoints([target, edgePoint]);
    const leaderMaterial = new THREE.LineBasicMaterial({ color: LEADER_COLOR });
    const leaderLine = new THREE.Line(leaderGeometry, leaderMaterial);
    group.add(leaderLine);

    // Small circle marker at target point — sabit 2 birim yarıçap
    const markerGeometry = new THREE.CircleGeometry(2, 16);
    const markerMaterial = new THREE.MeshBasicMaterial({ color: LEADER_COLOR });
    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.copy(target);
    group.add(marker);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 16;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, alphaTest: 0.05 });
    const sprite = new THREE.Sprite(spriteMaterial);

    sprite.scale.set(spriteWorldWidth, spriteWorldHeight, 1);
    sprite.center.set(0, 1); // anchor top-left
    sprite.position.copy(boxPos);
    group.add(sprite);

    group.userData = {
      id: generateUniqueId('callout'),
      type: 'callout',
      isSelectable: true,
      rawText: text,
      targetPoint: { x: target.x, y: target.y, z: target.z },
      boxPosition: { x: boxPos.x, y: boxPos.y, z: boxPos.z },
      layerName: activeLayerName,
    };

    return group;
  }, [camera, activeLayerName, userRole, getSpriteWorldSize, getNearestBoxEdgePoint]);

  // --- Main drawing effect ---
  useEffect(() => {
    if (!scene || !camera || !renderer || !snapPoints || commandType !== COMMANDS.DRAW_CALLOUT) return;

    const domElement = renderer.domElement;

    const handleMouseMove = (e) => {
      const rect = domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const worldPoint = new THREE.Vector3(mouse.x, mouse.y, 0).unproject(camera);
      const { finalPoint, snapped, snapSource } = getSnappedPoint(mouse, worldPoint, snapPoints, camera, renderer, { snapMode });

      // Snap marker
      if (snapMarkerRef.current) {
        scene.remove(snapMarkerRef.current);
        if (snapMarkerRef.current.geometry) snapMarkerRef.current.geometry.dispose();
        if (snapMarkerRef.current.material) snapMarkerRef.current.material.dispose();
        snapMarkerRef.current = null;
      }
      if (snapped && snapSource) {
        const m = createSnapMarker(snapSource);
        m.position.copy(snapSource.position);
        scene.add(m);
        snapMarkerRef.current = m;
        hoveredPointRef.current = snapSource.position.clone();
      } else {
        hoveredPointRef.current = worldPoint.clone();
      }

      // Leader preview during step 1
      if (step === 1 && targetPoint && hoveredPointRef.current) {
        drawLeaderPreview(targetPoint, hoveredPointRef.current);
      }
    };

    const handleClick = (e) => {
      if (cancelled) return;

      const rect = domElement.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // If input is visible (step 2), clicking outside finalizes
      if (step === 2 && inputVisible) {
        const inputEl = inputRef.current;
        if (inputEl) {
          const inputRect = inputEl.getBoundingClientRect();
          const isInside =
            e.clientX >= inputRect.left && e.clientX <= inputRect.right &&
            e.clientY >= inputRect.top && e.clientY <= inputRect.bottom;
          if (!isInside && inputValue.trim()) {
            finalize();
            return;
          } else if (!isInside && !inputValue.trim()) {
            cancelOperation();
            return;
          }
        }
        return;
      }

      if (!hoveredPointRef.current) return;
      const point = hoveredPointRef.current.clone();

      if (step === 0) {
        setTargetPoint(point);
        setStep(1);
        dispatch(setCommandMessage("Callout kutusunun konumunu secin"));
      } else if (step === 1) {
        setBoxPosition(point);
        setInputScreenPos({ x: clickX, y: clickY });

        if (tempLeaderRef.current) {
          scene.remove(tempLeaderRef.current);
          tempLeaderRef.current.geometry.dispose();
          tempLeaderRef.current.material.dispose();
          tempLeaderRef.current = null;
        }

        setInputVisible(true);
        setStep(2);
        dispatch(setCommandMessage("Notunuzu yazin, Enter ile bitirin"));
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        cancelOperation();
      }
    };

    domElement.addEventListener('mousemove', handleMouseMove);
    domElement.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      domElement.removeEventListener('mousemove', handleMouseMove);
      domElement.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
      if (snapMarkerRef.current) {
        scene.remove(snapMarkerRef.current);
        if (snapMarkerRef.current.geometry) snapMarkerRef.current.geometry.dispose();
        if (snapMarkerRef.current.material) snapMarkerRef.current.material.dispose();
        snapMarkerRef.current = null;
      }
    };
  }, [scene, camera, renderer, step, commandType, inputValue, inputVisible, cancelled]);

  // Send callout to server for live sync (uses refs for fresh values)
  const syncToServer = (userData) => {
    const pid = projectIdRef.current;
    if (!pid) {
      return;
    }
    postNote(pid, {
      calloutId: userData.id,
      rawText: userData.rawText,
      targetPoint: userData.targetPoint,
      boxPosition: userData.boxPosition,
      layerName: userData.layerName,
    }, shareTokenRef.current).catch(err => {
      console.warn('[Callout Sync] Not gonderimi basarisiz:', err.message);
    });
  };

  const finalize = () => {
    if (editingGroup) {
      // Edit mode: replace old callout with updated one
      const ud = editingGroup.userData;
      const target = new THREE.Vector3(ud.targetPoint.x, ud.targetPoint.y, ud.targetPoint.z);
      const boxPos = new THREE.Vector3(ud.boxPosition.x, ud.boxPosition.y, ud.boxPosition.z);

      // Remove old group from scene
      disposeGroup(editingGroup);
      scene.remove(editingGroup);

      // Create new group with updated text
      const newGroup = createCalloutGroup(target, boxPos, inputValue.trim());
      newGroup.userData.id = ud.id; // preserve original ID
      newGroup.userData.layerName = ud.layerName;
      scene.add(newGroup);

      const manager = window.undoRedoManager;
      if (manager) {
        const tx = manager.beginTransaction('Edit Callout');
        manager.addCommand(new DrawCommand(newGroup));
        manager.commitTransaction(tx);
      }

      // Sync edited note to server
      syncToServer(newGroup.userData);

      setEditingGroup(null);
      setInputVisible(false);
      setInputValue('');
      return;
    }

    if (targetPoint && boxPosition && inputValue.trim()) {
      const calloutGroup = createCalloutGroup(targetPoint, boxPosition, inputValue.trim());
      scene.add(calloutGroup);

      const manager = window.undoRedoManager;
      if (manager) {
        const tx = manager.beginTransaction('Draw Callout');
        manager.addCommand(new DrawCommand(calloutGroup));
        manager.commitTransaction(tx);
      }

      // Sync new note to server
      syncToServer(calloutGroup.userData);
    }

    // Reset for next callout
    setInputVisible(false);
    setInputValue('');
    setStep(0);
    setTargetPoint(null);
    setBoxPosition(null);
    dispatch(setCommandMessage("Dikkat cekmek istediginiz noktaya tiklayin"));
  };

  const cancelOperation = () => {
    setCancelled(true);
    setInputVisible(false);
    setStep(0);
    setInputValue('');
    setTargetPoint(null);
    setBoxPosition(null);
    setEditingGroup(null);
    if (tempLeaderRef.current) {
      scene.remove(tempLeaderRef.current);
      tempLeaderRef.current.geometry.dispose();
      tempLeaderRef.current.material.dispose();
      tempLeaderRef.current = null;
    }
    dispatch(resetOperation());
  };

  // Dispose all children in a group
  const disposeGroup = (group) => {
    group.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (child.material.map) child.material.map.dispose();
        child.material.dispose();
      }
    });
  };

  useEffect(() => {
    if (commandType === COMMANDS.DRAW_CALLOUT) {
      setCancelled(false);
    }
  }, [commandType]);

  // --- Double-click to edit existing callout ---
  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const domElement = renderer.domElement;

    const handleDblClick = (e) => {
      // Don't interfere if already in callout draw mode with active steps
      if (commandType === COMMANDS.DRAW_CALLOUT && step > 0) return;
      if (inputVisible) return;

      const rect = domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);

      // Collect all callout sprites for hit test
      const calloutSprites = [];
      scene.traverse((obj) => {
        if (obj.parent?.userData?.type === 'callout' && obj.isSprite) {
          calloutSprites.push(obj);
        }
      });

      const intersects = raycaster.intersectObjects(calloutSprites, false);
      if (intersects.length === 0) return;

      const hitSprite = intersects[0].object;
      const group = hitSprite.parent;
      if (!group?.userData?.type === 'callout') return;

      e.stopPropagation();

      // Enter edit mode — show textarea at the sprite's screen position
      const boxPos = new THREE.Vector3(
        group.userData.boxPosition.x,
        group.userData.boxPosition.y,
        group.userData.boxPosition.z
      );
      const screenPos = boxPos.clone().project(camera);
      const screenX = ((screenPos.x + 1) / 2) * rect.width;
      const screenY = ((-screenPos.y + 1) / 2) * rect.height;

      setEditingGroup(group);
      setInputValue(group.userData.rawText || '');
      setInputScreenPos({ x: screenX, y: screenY });
      setInputVisible(true);
    };

    domElement.addEventListener('dblclick', handleDblClick);
    return () => domElement.removeEventListener('dblclick', handleDblClick);
  }, [scene, camera, renderer, commandType, step, inputVisible]);

  // Auto-focus textarea
  useEffect(() => {
    if (inputVisible && inputRef.current) {
      inputRef.current.focus();
      // Select all text when editing
      if (editingGroup) {
        inputRef.current.select();
      }
    }
  }, [inputVisible, editingGroup]);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        finalize();
      }
    }
    if (e.key === 'Escape') {
      if (editingGroup) {
        // Cancel edit — just close without changes
        setEditingGroup(null);
        setInputVisible(false);
        setInputValue('');
      } else {
        cancelOperation();
      }
    }
    e.stopPropagation();
  };

  // Handle clicking outside textarea during edit mode
  useEffect(() => {
    if (!editingGroup || !inputVisible) return;

    const handleOutsideClick = (e) => {
      const inputEl = inputRef.current;
      if (!inputEl) return;
      const inputRect = inputEl.getBoundingClientRect();
      const isInside =
        e.clientX >= inputRect.left && e.clientX <= inputRect.right &&
        e.clientY >= inputRect.top && e.clientY <= inputRect.bottom;
      if (!isInside) {
        if (inputValue.trim()) {
          finalize();
        } else {
          setEditingGroup(null);
          setInputVisible(false);
          setInputValue('');
        }
      }
    };

    // Delay listener to avoid catching the dblclick itself
    const timer = setTimeout(() => {
      window.addEventListener('mousedown', handleOutsideClick);
    }, 100);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editingGroup, inputVisible, inputValue]);

  return (
    inputVisible && (
      <textarea
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleInputKeyDown}
        placeholder="Notunuzu yazin..."
        style={{
          position: 'absolute',
          top: inputScreenPos.y,
          left: inputScreenPos.x,
          width: `${BOX_WIDTH}px`,
          minHeight: `${BOX_HEIGHT}px`,
          zIndex: 1000,
          fontSize: `${FONT_SIZE}px`,
          fontFamily: 'Arial',
          padding: `${PADDING}px`,
          lineHeight: `${FONT_SIZE * 1.4}px`,
          color: TEXT_COLOR,
          resize: 'none',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          border: `2px solid ${TEXT_COLOR}`,
          outline: 'none',
        }}
      />
    )
  );
};

export default useDrawCallout;
